import { useState, useCallback } from 'react'
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, MenuItem, Select, InputLabel, Card, CardMedia } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useDropzone } from 'react-dropzone'

type ProcessingMethod = 'img2img' | 'inpainting' | 'outpainting' | 'remix'

export function ImageProcessingSelector({ onChange }: { onChange: (method: ProcessingMethod, images: string[]) => void }) {
  const [processingMethod, setProcessingMethod] = useState<ProcessingMethod>('img2img')
  const [sourceImages, setSourceImages] = useState<File[]>([])
  const [previews, setPreviews] = useState<string[]>([])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = [...sourceImages, ...acceptedFiles].slice(0, 5)
    setSourceImages(newFiles)

    newFiles.forEach((file, idx) => {
      const reader = new FileReader()
      reader.onload = () => {
        setPreviews(prev => {
          const copy = [...prev]
          copy[idx] = reader.result as string
          onChange(processingMethod, copy)
          return copy
        })
      }
      reader.readAsDataURL(file)
    })
  }, [sourceImages, processingMethod, onChange])

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    onDrop
  })

  const handleMethodChange = (method: ProcessingMethod) => {
    setProcessingMethod(method)
    onChange(method, previews)
  }

  return (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Image Processing</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <InputLabel id="processing-select-label">Method</InputLabel>
        <Select
          labelId="processing-select-label"
          value={processingMethod}
          onChange={e => handleMethodChange(e.target.value as ProcessingMethod)}
        >
          <MenuItem value="img2img">Img2Img</MenuItem>
          <MenuItem value="inpainting">Inpainting</MenuItem>
          <MenuItem value="outpainting">Outpainting</MenuItem>
          <MenuItem value="remix">Remix</MenuItem>
        </Select>

        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed #888',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            cursor: 'pointer'
          }}
        >
          <input {...getInputProps()} />
          {sourceImages.length > 0 ? (
            <Typography>{sourceImages.map(f => f.name).join(', ')}</Typography>
          ) : (
            <Typography>Drag & drop up to 5 source images here, or click to select</Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {previews.map((src, idx) => (
            <Card key={idx} sx={{ width: 80, height: 80, borderRadius: 1, overflow: 'hidden' }}>
              <CardMedia component="img" image={src} alt={`preview-${idx}`} sx={{ objectFit: 'cover' }} />
            </Card>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}
